import { useCallback } from 'react';
import { SimulationConfig, Ball, Vector2D, EscapeRing, BlastEvent, ShapeType } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { BlastEventPool } from '@/services/BlastEventPool';

export const useAudioTriggers = (
  configRef: React.MutableRefObject<SimulationConfig>,
  escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
  initialEscapeRingCountRef: React.MutableRefObject<number>,
  blastEventsRef: React.MutableRefObject<BlastEvent[]>,
  blastEventPoolRef: React.MutableRefObject<BlastEventPool>,
  morphStateRef: React.MutableRefObject<{ active: boolean, from: ShapeType, to: ShapeType, progress: number }>,
  containerRadiusRef: React.MutableRefObject<number>,
  shakeRef: React.MutableRefObject<number>,
  isRotationStoppedRef: React.MutableRefObject<boolean>,
  setBounceCount: React.Dispatch<React.SetStateAction<number>>,
  counterPopRef: React.MutableRefObject<number>
) => {
  const playCollisionSound = useCallback((isDestruction: boolean = false) => {
    const cfg = configRef.current;
    if (cfg.ballCollisionSoundType === 'SOUND_EFFECT') AUDIO.playSoundEffect(cfg.selectedBallSoundEffect, cfg, cfg.collisionVolume);
    else if (cfg.ballCollisionSoundType === 'CUSTOM') {
      const customSound = cfg.userSounds.find((s: any) => s.id === cfg.selectedUserSoundId);
      if (customSound) {
          const source = customSound.url || customSound.base64;
          if (source) AUDIO.playCustomBuffer(source, cfg.collisionVolume, cfg, false);
      }
      else AUDIO.playNote(cfg.selectedMelody, cfg, 1.0);
    } 
    else if (cfg.ballCollisionSoundType === 'SONG_SLICE') {
        if (!isDestruction) {
            AUDIO.playNextSlice(cfg);
        }
    }
    else AUDIO.playNote(cfg.selectedMelody, cfg, 1.0);
  }, [configRef]);

  const playBreakSound = useCallback(() => {
    const cfg = configRef.current;
    let type = cfg.breakSoundType;
    let effect = cfg.selectedBreakSoundEffect;
    let customId = cfg.selectedBreakUserSoundId;

    if (cfg.mode === 'ESCAPE' && cfg.enableBreakSoundZones && cfg.breakSoundZones.length > 0) {
        const total = initialEscapeRingCountRef.current || cfg.escapeRingCount;
        const current = escapeRingsRef.current.length; 
        const destroyedCount = total - current + 1; 
        let pct = (destroyedCount / total) * 100;
        pct = Math.max(0.1, Math.min(100, pct)); 

        const zones = [...cfg.breakSoundZones].sort((a, b) => a.threshold - b.threshold);
        let zone = zones.find((z: any) => pct <= z.threshold);
        if (!zone && zones.length > 0) zone = zones[zones.length - 1];
        
        if (zone) {
            type = zone.soundType;
            effect = zone.soundEffect;
            customId = zone.customSoundId;
        }
    }

    if (type === 'SOUND_EFFECT') AUDIO.playSoundEffect(effect, cfg, cfg.collisionVolume * 1.5, true);
    else if (type === 'CUSTOM') {
      const customSound = cfg.userSounds.find((s: any) => s.id === customId);
      if (customSound) {
          const source = customSound.url || customSound.base64;
          if (source) AUDIO.playCustomBuffer(source, cfg.collisionVolume * 1.5, cfg, true, true);
      }
      else AUDIO.playSoundEffect(effect, cfg, cfg.collisionVolume * 1.5, true);
    } else AUDIO.playNote(cfg.selectedMelody, cfg, 1.5, true);
  }, [configRef, initialEscapeRingCountRef, escapeRingsRef]);

  const triggerBlast = useCallback((b: {pos: Vector2D, color: string, radius: number}, cmr?: number, gapAngle?: number, morph?: { from: ShapeType, to: ShapeType, factor: number }, animType?: string) => {
    const cfg = configRef.current;
    
    // If morph data is passed, use it. Otherwise, fallback to current state.
    const currentMorphData = morph || (morphStateRef.current.active ? {
        from: morphStateRef.current.from,
        to: morphStateRef.current.to,
        factor: morphStateRef.current.progress,
        baseRadius: cfg.containerRadius
    } : {
        from: cfg.containerShape,
        to: cfg.containerShape,
        factor: 0,
        baseRadius: cfg.containerRadius
    });

    const id = Math.random().toString();
    const startTime = Date.now();
    const duration = cfg.blastDuration;
    const maxRadius = (cmr !== undefined) ? cmr : containerRadiusRef.current * 1.5;
    const color = b.color;
    const animTypeVal = animType || cfg.escapeBreakAnimation;
    const intensity = 1.0;
    const shape = currentMorphData.from;
    const rotation = cfg.containerRotation;

    let newEvent: BlastEvent;
    if (blastEventPoolRef && blastEventPoolRef.current) {
        newEvent = blastEventPoolRef.current.get(
            id,
            b.pos,
            startTime,
            duration,
            maxRadius,
            color,
            animTypeVal,
            intensity,
            shape,
            rotation
        );
        newEvent.gapAngle = gapAngle;
        newEvent.morphData = currentMorphData;
    } else {
        newEvent = {
          id, pos: { ...b.pos }, startTime, duration,
          maxRadius, color, animationType: animTypeVal, intensity,
          shape, 
          rotation,
          gapAngle: gapAngle,
          morphData: currentMorphData
        };
    }
    blastEventsRef.current.push(newEvent);

    const maxBlasts = cfg.maxActiveBlasts ?? 30; 
    if (blastEventsRef.current.length > maxBlasts) {
        const released = blastEventsRef.current.slice(0, blastEventsRef.current.length - maxBlasts);
        released.forEach(e => {
            if (blastEventPoolRef && blastEventPoolRef.current) {
                blastEventPoolRef.current.release(e);
            }
        });
        blastEventsRef.current = blastEventsRef.current.slice(blastEventsRef.current.length - maxBlasts);
    }

    playBreakSound();
    if (cfg.mode === 'ESCAPE') shakeRef.current = 15 * cfg.shakeIntensityMultiplier; 
    if (cfg.escapeStopRotationOnHit && cfg.mode === 'ESCAPE') isRotationStoppedRef.current = true;
  }, [configRef, morphStateRef, blastEventsRef, containerRadiusRef, playBreakSound, shakeRef, isRotationStoppedRef]);

  return { playCollisionSound, playBreakSound, triggerBlast };
};
